import {
  FULFILLMENT_DIMENSIONS_LABELS,
  REFINEMENTS,
  GET_IT_FAST_DIMENSION_LABEL,
  RATINGS_REFINEMENT_KEY,
  HIGHLY_RATED_REFINEMENT_KEY,
  MAX_REFINEMENTS,
  BEST_RATED,
  HIGH_RATED,
  REGEX_PATTERNS,
  searchSortTypes,
  browseSortTypes,
  FIT_COMPATIBILITY_DIMENSIONS,
  FULFILLMENT_LABELS,
  IN_STORE_CHIP_DIMENSION_LABEL,
  PLP_SPEED_DIMENSION_LABEL
} from './utils/constants';

import {
  filterDimensionsByLabel,
  createFulfillmentDimension
} from './utils/fulfillment-helpers';

export const PICK_UP_TODAY_REFINEMENT_KEY = '1z175a5';
export const FILTER_LABEL = 'All Filters';
export const FILTER_SORT_LABEL = 'Filter & Sort';

// Dimension name constants
const BROWSE_DIMENSION_NAMES = {
  REVIEW_RATING: 'REVIEW RATING',
  SOUND_RATING: 'SOUND RATING RANGE (DECIBELS)',
  ECO_FEATURES: 'ECO FEATURES',
  INDOOR_OUTDOOR: 'INDOOR/OUTDOOR',
  CORDLESS_CORDED: 'CORDLESS/ CORDED',
  HOME_DEPOT_EVENTS: 'THE HOME DEPOT EVENTS',
  CATEGORY: 'CATEGORY',
  STACKABLE: 'STACKABLE',
  BRAND: 'BRAND',
  COMMERCIAL_RESIDENTIAL: 'COMMERCIAL / RESIDENTIAL',
  INSTALLATION: 'INSTALLATION',
  LET_US_DO_IT: 'LET US DO IT FOR YOU'
};

export const ScreenSize = {
  UNPREFIXED: 'unprefixed',
  SMALL: 'sm',
  MEDIUM: 'md',
  LARGE: 'lg',
  X_LARGE: 'xl',
  DX_LARGE: '2xl'
};

/**
 * Formats the results count with a cap at 1000+
 * @param {number} count - The total number of results
 * @returns {string} Formatted count string (e.g., "1,000+" or "523")
 */
export const formatToFriendlyDisplayCount = (count) => {
  if (!Number.isFinite(count)) {
    return '0';
  }

  if (count > 1000) {
    return '1,000+';
  }

  return count.toLocaleString();
};

/**
 * check if catStyle is in the currentUrl and then return new url with catStyle "showproducts"
 * appended to prevent redirect to category page version of PLP
 * @param url
 * @param querySeparator
 */
export function filterCatStyleCheckAndReturnUrl(url) {
  let newUrl = '';
  let querySeparator = '?';

  if (url !== null) {
    newUrl = url;
  }
  if (newUrl.includes('?')) {
    querySeparator = '&';
  }

  // Check if 'catStyle' exists in current url
  if (window.location.href.includes('catStyle=')) {
    const currentUrlParams = new URLSearchParams(window.location.search);
    if (currentUrlParams.get('catStyle').toUpperCase() === 'SHOWPRODUCTS') {
      newUrl = `${url}${querySeparator}catStyle=${currentUrlParams.get('catStyle')}`;
    }
  }
  return newUrl;
}

export function extractNavParamFromClearAllURL(data) {
  const clearAllRefinementsURL = data?.searchModel?.metadata?.clearAllRefinementsURL;
  return clearAllRefinementsURL?.match(/\/(N-[^/?]+)/)?.[1]?.slice(2) || '';
}

export function hasFitCompatibility(label) {
  return FIT_COMPATIBILITY_DIMENSIONS.includes(label?.toUpperCase());
}

export const showMoreFilters = ({ dimensions = [], breadCrumbs = [] }) => {
  const dimensionCategories = filterDimensionsByLabel(dimensions, 'CATEGORY');
  const breadCrumbsCategories = breadCrumbs.filter((breadCrumb) => (breadCrumb.dimensionName || '')
    && breadCrumb.dimensionName.toUpperCase() === 'CATEGORY');

  if ((breadCrumbsCategories === undefined || breadCrumbsCategories.length === 0)
    && (dimensionCategories !== undefined && dimensionCategories.length === 1)) {
    return true;
  }
  return false;
};

export const getNavParam = ({ nValue, breadCrumbs }) => {
  if (nValue) {
    return nValue.trim();
  }
  if (breadCrumbs) {
    const breadCrumbsCategories = (breadCrumbs.filter((breadCrumb) => (breadCrumb.dimensionName || '') && breadCrumb.dimensionName === 'Category')); // eslint-disable-line max-len

    const { url } = breadCrumbsCategories.pop() || {};

    if (!url) return null;

    return /\/N-(\w+)/.exec(url)[1];
  }
  return null;
};

export const getLoadingState = ({ data, loading, props, prevProps }) => {
  const varChange = (prevProps?.navParam !== props?.navParam)
    || (prevProps?.keyword !== props?.keyword)
    || (prevProps?.nao !== props?.nao)
    || (prevProps?.sortby !== props?.sortby)
    || (prevProps?.sortorder !== props?.sortorder)
    || (prevProps?.lowerbound !== props?.lowerbound)
    || (prevProps?.upperbound !== props?.upperbound);

  // loading is false
  if (!loading) return false;

  // loading is true, no data
  if (!data) return true;

  // loading is true, data, variable change
  if (data && varChange) return true;

  return false;
};

export const updateQueryParams = (path, params) => {
  const paramNames = params.map((p) => p.paramName); // eslint-disable-line id-length

  const [pathname, query = ''] = path.split('?');
  const queryParams = query.split('&').filter((p) => p); // eslint-disable-line id-length
  const updatedParams = [
    ...queryParams
      .filter((queryParam) => {
        const parts = queryParam.split('=');
        return paramNames.indexOf(parts[0]) === -1;
      }),
    ...params
      .filter((p) => p.paramValue) // eslint-disable-line id-length
      .map((param) => {
        return `${param.paramName}=${param.paramValue}`;
      })
  ].join('&');
  return pathname + '?' + updatedParams;
};

export const parseUrl = ({ path }) => {
  const parts = path.replace(/^\/render[^/]*/, '').split('?');
  const leftOfQuery = parts[0] || '';
  let queryString = parts[1] || '';

  let [_, context, category, ...pieces] = leftOfQuery.split('/');

  const slugs = [];
  let keyword;
  let nValue;

  if (queryString && (queryString.indexOf('lowerBound') > -1 && queryString.indexOf('upperBound') > -1)) {
    queryString = queryString.replace('lowerBound', 'lowerbound');
    queryString = queryString.replace('upperBound', 'upperbound');
  }

  if (context === 's') {
    keyword = category;
    category = 'search';
  }
  if (/^N-/.test(category)) {
    nValue = category;
    category = null;
  }
  (pieces || []).forEach((piece) => {
    if (!/^Ntt-|^Ntk-|N-5/.test(piece)) {
      slugs.push(piece);
    } else if (/^Ntt-/.test(piece)) {
      keyword = piece.substring(4, piece.length)?.split(/\+|%2B/).join(' ');
    } else if (/^N-/.test(piece)) {
      nValue = piece.substring(2, piece.length);
    }
  });

  const params = (queryString.match(/(\w+)=([^&]+)/g) || [])
    .map((val) => {
      const param = val.split('=');
      return {
        [param[0]]: param[1]
      };
    })
    .reduce((acc, next) => {
      const key = Object.keys(next)[0];
      return {
        ...acc,
        [key]: next[key]
      };
    }, {});

  if (params.storeIds) {
    params.storeIds = params.storeIds.split(',');
  }

  if (keyword) {
    if (keyword.match(/^https?:/)) {
      keyword = '';
    }
  }

  return {
    slugs,
    keyword,
    context,
    category,
    nValue,
    ...params
  };
};

export function getClearAllWithSort({ metadata, searchReport }) {
  let url = metadata.clearAllRefinementsURL || '';
  const { sortBy, sortOrder } = searchReport;
  const defaultSortOrder = sortOrder && sortOrder !== 'none' ? sortOrder : 'asc';

  url = filterCatStyleCheckAndReturnUrl(url);
  let querySeparator = '?';
  if (url.includes('?')) {
    querySeparator = '&';
  }

  if (defaultSortOrder && sortBy) {
    url += `${querySeparator}sortorder=${defaultSortOrder}&sortby=${sortBy}`;
  }
  return url;
}

export const getCategories = (dimensions) => {
  const dimensionCategories = filterDimensionsByLabel(dimensions, 'CATEGORY');
  if (dimensionCategories === undefined || dimensionCategories.length === 0) {
    return [{ label: 'Category', dimensionId: '2', refinements: [] }];
  }
  return dimensionCategories;
};

export function sortDimensions({
  dimensions = [],
  searchReport = {},
  metadata = {},
  shiftCategoryDimension = false,
  showSmartFilter = false,
  isPlpSpeedEnabled = false
}) {

  let speedDimension;

  // If PLP speed is enabled, update dimensions to replace Get It Fast with fulfillment options
  // Pending: Consider edge case where isPlpSpeedEnabled is true but DELIVERY/FREE DELIVERY dimensions are missing
  // and only Get It Fast is present
  if (!isPlpSpeedEnabled) {
    speedDimension = filterDimensionsByLabel(dimensions, 'GET IT FAST');
  } else {
    speedDimension = createFulfillmentDimension(dimensions);
  }

  const inStockOnly = filterDimensionsByLabel(dimensions, 'AVAILABILITY');

  const categories = getCategories(dimensions);

  let smartFilter = [];

  const moreDimensions = dimensions.filter((dimension) => (dimension.label)
    && [
      'GET IT FAST',
      'CATEGORY',
      'AVAILABILITY',
      FULFILLMENT_DIMENSIONS_LABELS.delivery,
      FULFILLMENT_DIMENSIONS_LABELS.freeDelivery
    ]
      .indexOf(dimension.label.toUpperCase()) === -1);

  if (showSmartFilter) {
    smartFilter = [{
      label: 'Smart Filters',
      dimensionId: 'fakeSmartFilterDimId',
      refinements: []
    }];
  }

  if (!isPlpSpeedEnabled && speedDimension && !speedDimension.length) {
    speedDimension = [{
      label: GET_IT_FAST_DIMENSION_LABEL,
      dimensionId: 'fakeDimension',
      refinements: [
        {
          label: REFINEMENTS.PICKUP_TODAY,
          refinementKey: '1z175a5',
          recordCount: 0,
          url: searchReport?.searchUrl || metadata?.canonicalUrl
        }]
    }];
  }
  if (shiftCategoryDimension) {
    return [
      ...categories,
      ...speedDimension,
      ...smartFilter,
      ...inStockOnly,
      ...moreDimensions
    ];
  }
  return [
    ...speedDimension,
    ...smartFilter,
    ...inStockOnly,
    ...categories,
    ...moreDimensions
  ];
}

export function getRefinementUrlWithSort({
  dimension, metadata, refinement, searchReport = {},
}) {
  const { dimensionId } = dimension;
  let url = refinement?.url?.slice(0);

  if (dimensionId === 'custom_price') {
    const parts = metadata.canonicalUrl.split('?');
    if (parts.length > 1) {
      url = metadata.canonicalUrl + `&upperbound=${refinement.upperbound}&lowerbound=${refinement.lowerbound}`;
    } else {
      url = metadata.canonicalUrl + `?NCNI-5&upperbound=${refinement.upperbound}&lowerbound=${refinement.lowerbound}`;
    }
  }
  if (url && (url.indexOf('lowerBound') > -1 && url.indexOf('upperBound') > -1)) {
    url = url.replace('lowerBound', 'lowerbound');
    url = url.replace('upperBound', 'upperbound');
  }

  const urlParts = url.split('/');
  const lastPart = urlParts.length ? urlParts[urlParts.length - 1] : '';
  const params = lastPart.split('?');
  const refinementParam = params.length > 1 ? params.pop() : null;
  const { sortBy, sortOrder } = searchReport;

  if (dimension?.label?.toUpperCase() !== 'CATEGORY') {
    url = filterCatStyleCheckAndReturnUrl(url);
  }

  let querySeparator = '?';
  if (url.includes('?')) {
    querySeparator = '&';
  }

  let updatedQuery = '';
  if (sortBy && sortOrder) {
    const isDefaultSort = sortBy === 'topsellers' && sortOrder === 'desc';
    if (!isDefaultSort) {
      updatedQuery = `${querySeparator}sortby=${sortBy}&sortorder=${sortOrder}`;
    }
  } else if (refinementParam) {
    updatedQuery = `?${refinementParam}`;
  }

  let updatedUrl = `${url}${updatedQuery}`;
  if (refinementParam) {
    updatedUrl = `${url}${updatedQuery.replace('?', '&')}`;
  }

  return updatedUrl;
}

export function hasDimensionName(dimensionName) {
  const DIMENSION_NAMES = ['MATERIAL', 'STYLE', 'COLOR', 'PATTERN', 'MEASUREMENTS', 'SIZE'];
  return DIMENSION_NAMES.includes(dimensionName);
}

const getCrumbsByLabel = (crumbs, label) => (
  crumbs.filter((crumb) => (crumb.label || '').toUpperCase() === label.toUpperCase())
);

// Helper functions for getBrowseSearchParam
const addLabelToSet = (label, labels, addedLabels) => {
  if (label && !addedLabels.has(label)) {
    labels.push(label);
    addedLabels.add(label);
  }
};

const addSingleDimensionLabelToSet = (dimension, labels, addedLabels) => {
  if (dimension && dimension.length === 1) {
    addLabelToSet(dimension[0].label, labels, addedLabels);
  }
};

const processDimensionPatternMatch = (crumbs, pattern, labels, addedLabels) => {
  const matches = crumbs.filter((crumb) => (crumb.dimensionName || '').toUpperCase().includes(pattern)
  );
  addSingleDimensionLabelToSet(matches, labels, addedLabels);
};

export function getBrowseSearchParam(crumbs = []) {
  const labels = [];
  const addedLabels = new Set();
  const visNavCategoryMap = new Map();

  const crumbsByDimension = new Map();
  const visualNavCrumbs = [];
  let pickUpToday = null;

  crumbs.forEach((crumb) => {
    const dimensionName = (crumb.dimensionName || '').toUpperCase();

    // Categorize by dimension
    if (!crumbsByDimension.has(dimensionName)) {
      crumbsByDimension.set(dimensionName, []);
    }
    crumbsByDimension.get(dimensionName).push(crumb);

    // Check for visual nav dimensions
    if (hasDimensionName(dimensionName)) {
      visualNavCrumbs.push(crumb);
    }

    if (crumb?.refinementKey === PICK_UP_TODAY_REFINEMENT_KEY) {
      pickUpToday = crumb;
    }
  });

  const reviewRatings = crumbsByDimension.get(BROWSE_DIMENSION_NAMES.REVIEW_RATING) || [];
  if (reviewRatings.length === 1) {
    if (reviewRatings[0].label === '5') {
      addLabelToSet('Best Rated', labels, addedLabels);
    } else if (reviewRatings[0].label === '4 & Up') {
      addLabelToSet('Highly Rated', labels, addedLabels);
    }
  }

  addSingleDimensionLabelToSet(crumbsByDimension.get(BROWSE_DIMENSION_NAMES.SOUND_RATING), labels, addedLabels);
  addSingleDimensionLabelToSet(crumbsByDimension.get(BROWSE_DIMENSION_NAMES.ECO_FEATURES), labels, addedLabels);
  addSingleDimensionLabelToSet(crumbsByDimension.get(BROWSE_DIMENSION_NAMES.INDOOR_OUTDOOR), labels, addedLabels);
  addSingleDimensionLabelToSet(crumbsByDimension.get(BROWSE_DIMENSION_NAMES.CORDLESS_CORDED), labels, addedLabels);

  const homeDepotEvents = crumbsByDimension.get(BROWSE_DIMENSION_NAMES.HOME_DEPOT_EVENTS) || [];
  if (homeDepotEvents.length === 1) {
    addLabelToSet(homeDepotEvents[0].label, labels, addedLabels);
  }

  const categoryCrumbs = crumbsByDimension.get(BROWSE_DIMENSION_NAMES.CATEGORY) || [];
  const hasWashersAndDryersRefinement = categoryCrumbs.length > 0
    && getCrumbsByLabel(categoryCrumbs, 'WASHERS & DRYERS').length === 1;
  const stackableRefinements = crumbsByDimension.get(BROWSE_DIMENSION_NAMES.STACKABLE) || [];
  if (
    hasWashersAndDryersRefinement
    && stackableRefinements.length === 1
    && stackableRefinements[0].label.toUpperCase() === 'STACKABLE'
  ) {
    addLabelToSet('Stackable', labels, addedLabels);
  }

  visualNavCrumbs.forEach((crumb) => {
    if (visNavCategoryMap.has(crumb.dimensionName)) {
      visNavCategoryMap.delete(crumb.dimensionName);
    } else {
      visNavCategoryMap.set(crumb.dimensionName, crumb.label);
    }
  });

  processDimensionPatternMatch(crumbs, 'SIZE', labels, addedLabels);
  processDimensionPatternMatch(crumbs, 'MEASUREMENTS', labels, addedLabels);
  processDimensionPatternMatch(crumbs, 'SHAPE', labels, addedLabels);
  processDimensionPatternMatch(crumbs, 'COLOR', labels, addedLabels);
  processDimensionPatternMatch(crumbs, 'PATTERN', labels, addedLabels);
  processDimensionPatternMatch(crumbs, 'STYLE', labels, addedLabels);
  processDimensionPatternMatch(crumbs, 'MATERIAL', labels, addedLabels);

  addSingleDimensionLabelToSet(crumbsByDimension.get(BROWSE_DIMENSION_NAMES.BRAND), labels, addedLabels);
  addSingleDimensionLabelToSet(
    crumbsByDimension.get(BROWSE_DIMENSION_NAMES.COMMERCIAL_RESIDENTIAL),
    labels,
    addedLabels
  );

  // Add visual nav category labels
  visNavCategoryMap.forEach((value) => addLabelToSet(value, labels, addedLabels));

  // Process category names
  if (categoryCrumbs.length > 0) {
    const categoryName = (categoryCrumbs[categoryCrumbs.length - 1] || '').label;
    // Only add if not already in labels (case-insensitive)
    const labelsUpper = labels.join(' ').toUpperCase();
    if (!labelsUpper.includes(categoryName.toUpperCase())) {
      addLabelToSet(categoryName, labels, addedLabels);
    }
  }

  // Process pickup today for in-stock
  const isFurniture = categoryCrumbs.find((category) => category.label === 'Furniture');
  if (pickUpToday && !isFurniture && categoryCrumbs.length > 0) {
    labels.push('In Stock Near Me');
  }

  let result = labels.join(' ');

  const installationNames = crumbsByDimension.get(BROWSE_DIMENSION_NAMES.INSTALLATION) || [];
  if (installationNames.length === 1) {
    result += '  - ' + installationNames[0].label;
  }

  const letUsDoItForYouLabels = crumbsByDimension.get(BROWSE_DIMENSION_NAMES.LET_US_DO_IT) || [];
  if (letUsDoItForYouLabels.length === 1) {
    result += ' - ' + letUsDoItForYouLabels[0].label;
  }

  return result;
}

export function getCustomPriceRange(lowerbound, upperbound) {
  let lbound = parseInt(lowerbound, 10) || undefined;
  let ubound = parseInt(upperbound, 10) || undefined;

  lbound = !lbound && !!ubound ? 0 : lbound;
  // pass the ubound as received (undef), if not supplied by user
  ubound = !ubound && !!lbound ? upperbound : ubound;

  if (ubound && lbound > ubound) [lbound, ubound] = [ubound, lbound];
  return { lbound, ubound };
}

export function getRefinementIndex(refinement, dimension) {
  const { refinements = [] } = dimension;
  let index = refinements.findIndex((ref) => {
    return ref.label === refinement.label;
  });
  return index + 1;
}

export const getRatingsMessage = (breadCrumbs) => {
  let message;
  for (let i = 0; i < breadCrumbs.length; i += 1) {
    const isReviewRating = breadCrumbs[i].dimensionName
      && breadCrumbs[i].dimensionName.toUpperCase() === BROWSE_DIMENSION_NAMES.REVIEW_RATING;
    if (isReviewRating && breadCrumbs[i].label === '5') {
      message = 'Shop products with the best ratings based on our customer reviews';
      break;
    } else if (isReviewRating && breadCrumbs[i].label === '4 & Up') {
      message = 'Shop highly rated products based on our customer reviews';
      break;
    }
  }
  return message;
};

export const hasComparator = (refinement) => {
  const label = refinement?.label;
  return label ? ['Less', 'Greater', '<', '>', 'Other'].some((substring) => label.includes(substring)) : false;
};

export const hasRange = (refinement) => {
  const label = refinement?.label;
  return label ? label.includes('-') : false;
};

export const getRefinementLabel = (refinement) => {
  if (refinement && typeof refinement === 'object') {
    const label = refinement.label;
    if (hasComparator(refinement)) {
      return label.replace(/\D/g, '');
    }
    return label;
  }
  return '';
};

export const isSeries = (sortedRefinements) => {
  let isContinuous = true;
  let refinementsArr = [...sortedRefinements];

  // check to see if range comparison is applicable to all applied refinement
  if (sortedRefinements.every((ref) => ref.label.includes('-'))) {
    refinementsArr.every((el, index, arr) => {
      const curr = getRefinementLabel(el);
      // if there is no previous refinement, set the current refinement as the comparison
      const prev = getRefinementLabel((arr[index - 1])) || curr.split(' ')[0];

      isContinuous = !(prev.split(' ').slice(-1)[0] !== curr.split(' ')[0] && curr !== '');
      // reference the value of isContinuous to determine whether or not the function should continue iterating
      // refinementsArr
      // the function will eject once isContinuous is assessed as false
      return isContinuous;
    });
  }
  // and finally, return the value so to the original call
  return isContinuous;
};

// Handle "less than" cases first
const extractLessThanUnit = (afterLessThan) => {
  if (!Number.isNaN(Number(afterLessThan.trim()))) {
    return '';
  }

  const parenthesesMatch = afterLessThan.match(REGEX_PATTERNS.parentheses);
  if (parenthesesMatch) {
    return parenthesesMatch[1];
  }

  const plainUnitMatch = afterLessThan.match(REGEX_PATTERNS.plainUnit);
  return plainUnitMatch ? plainUnitMatch[1] : '';
};

// Handle "or greater" cases
const extractGreaterUnit = (beforeOr) => {
  if (beforeOr.split(' ').length <= 1) {
    return '';
  }

  const match = REGEX_PATTERNS.general.exec(beforeOr);
  return match ? (match[2] || match[3] || '').trim() : '';
};

export const extractUnitFromInput = (input) => {
  const lowerInput = input.toLowerCase();

  if (lowerInput.includes('less than')) {
    return extractLessThanUnit(input.split('less than ')[1]);
  }

  if (lowerInput.includes('or greater')) {
    return extractGreaterUnit(input.split(' or ')[0]);
  }

  const match = REGEX_PATTERNS.general.exec(input);
  return match ? (match[2] || match[3] || '').trim() : '';
};

export const hasSpecialWord = (label) => {
  const specialWords = ['greater', 'Greater', 'less', 'Less', 'Others', 'others'];
  return specialWords.some((word) => label.includes(word));
};

export const cleanUrl = (url, refinements) => {
  return refinements.reduce((cleanedUrl, ref) => {
    const navParam = ref.label.replace('-', '').split(' ')
      .filter((param) => param !== '').join('-') + '/';
    const nValue = `Z${ref.refinementKey}`;
    return cleanedUrl.replace(navParam, '').replace(nValue, '');
  }, url);
};

export const getRefinementRange = (startRef, endRef, unit) => {
  const startRange = hasComparator(startRef) || hasSpecialWord(startRef.label)
    ? startRef.label
    : startRef.label.split(' ')[0];

  const endRange = hasComparator(endRef) || hasSpecialWord(endRef.label)
    ? endRef.label
    : endRef.label.split(' ')[0];

  if (hasSpecialWord(endRef.label)) {
    return `${startRange}${unit} - ${endRange}`;
  }
  return `${startRange}${unit} - ${endRange}${unit}`;
};

export const getAppliedRefinementsRange = ({
  refinements,
  dimension = {},
  label
}) => {
  if (!dimension || !refinements?.length) {
    return null;
  }

  const [initialRefinement, ...rest] = refinements;
  const endRefinement = rest[rest.length - 1] || initialRefinement;

  const unit = hasSpecialWord(endRefinement.label)
    ? extractUnitFromInput(initialRefinement.label)
    : extractUnitFromInput(endRefinement.label);

  return {
    dimensionName: label,
    refinementLabel: getRefinementRange(initialRefinement, endRefinement, unit),
    url: cleanUrl(initialRefinement.url, refinements)
  };
};

export const parseLabel = (label) => {
  if (typeof label === 'string' && label.split(' ').length === 1) return parseFloat(label);
  return '';
};

export const sortRefinements = (currentRef, nextRef) => {
  if (currentRef || nextRef) {

    if (currentRef.label.toUpperCase().includes('GREATER') || currentRef.label.startsWith('>')) return 1;
    if (currentRef.label.toUpperCase().includes('LESS') || currentRef.label.startsWith('<')) return -1;

    const unitValueA = parseLabel(getRefinementLabel(currentRef).split(' ')[0]);
    const unitValueB = parseLabel(getRefinementLabel(nextRef).split(' ')[0]);
    return unitValueA - unitValueB;
  }
  return 0;
};

export const getAppliedRefinements = (appliedDimensions = [],
  isGetItFastOnPlp = false) => {
  let appliedRefinements = [];
  // eslint-disable-next-line
  appliedDimensions?.forEach((dimension) => {
    let { label, refinements, isNumericFilter, } = dimension;
    if (label === 'Special Values') label = 'Price';

    if (isNumericFilter) {
      let sortedRefinements = [...refinements].sort(sortRefinements);
      // check to see if a user inputted range is a series (ie. the next refinement starts with the upperbound value
      // of the last)
      if (sortedRefinements.length > 1 && isSeries(sortedRefinements)) {
        const appliedRefinementRange = getAppliedRefinementsRange({
          refinements: sortedRefinements,
          dimension,
          label
        });
        if (appliedRefinementRange) {
          appliedRefinements.push(appliedRefinementRange);
          return;
        }
      }
    }

    refinements.forEach((refinement) => {
      let { label: refinementLabel, refinementKey } = refinement;
      if (refinementLabel === '5' && refinementKey === RATINGS_REFINEMENT_KEY) {
        refinementLabel = 'Best Rated';
      }
      if (refinementLabel === '4 & Up' && refinementKey === HIGHLY_RATED_REFINEMENT_KEY) {
        refinementLabel = 'Highly Rated';
      }
      appliedRefinements.push({ dimensionName: label, refinementLabel, ...refinement });
    });
  });
  return appliedRefinements;
};

const getDimension = (dimensionName, _dimensions) => {
  let otherDimensions = [];
  let dimensionList = _dimensions.filter((dimension) => {
    let isIncluded = dimension.label?.includes(dimensionName);
    if (!isIncluded) {
      otherDimensions.push(dimension);
    }
    return isIncluded;
  });
  return [dimensionList, otherDimensions];
};

export const sortAscending = (refinementA, refinementB) => {
  if (refinementA.label.toUpperCase() > refinementB.label.toUpperCase()) {
    return 1;
  }
  if (refinementA.label.toUpperCase() < refinementB.label.toUpperCase()) {
    return -1;
  }
  return 0;
};

export const decodeBase36RefinementKey = (refinementKeys) => {
  return refinementKeys.map((refinementKey) => parseInt(refinementKey, 36));
};

export const encodeBase36RefinementIds = (refinementIds) => {
  return refinementIds.map((refinementId) => refinementId.toString(36));
};

export const sortRefinementKeys = (refinements) => {
  const cleanRefienementList = refinements.filter((refinement) => refinement !== undefined && refinement !== null);
  let decodedRefinements = decodeBase36RefinementKey(cleanRefienementList);
  decodedRefinements.sort((valueA, valueB) => valueA - valueB);
  return encodeBase36RefinementIds(decodedRefinements);
};

export const getRefinementsByDimension = (appliedDimensions, dimension) => {
  // eslint-disable-next-line max-len
  return appliedDimensions?.find((appliedDimension) => appliedDimension.label.toUpperCase() === dimension.label.toUpperCase())?.refinements || [];
};

// sort based on the priroity defined for seo
export const sortSelectedDimensions = (selectedDimensions) => {
  let otherDimensions;
  // get Review Ratings
  const [reviewRatings, excludeReviewRatings] = getDimension('Review Rating', selectedDimensions);
  const priority = reviewRatings[0]?.refinements.find(
    (ref) => ref.label === '5' || ref.label === '4 & Up');
  let priorityDimension = reviewRatings;
  if (priority === undefined) {
    priorityDimension = [];
    /* if rating is not 5 or 4 & up it need to go at the end of the list
    that's why it is added back to the list here */
    excludeReviewRatings.push(...reviewRatings);
  }
  // return brand
  const [brand, excludeBrand] = getDimension('Brand', excludeReviewRatings);
  // return color related
  const [color, excludeColor] = getDimension('Color', excludeBrand);
  color.sort(sortAscending);
  // return finsh
  const [finish, excludeFinish] = getDimension('Finish', excludeColor);
  finish.sort(sortAscending);
  // return size
  const [size, excludeSize] = getDimension('Size', excludeFinish);
  size.sort(sortAscending);
  // rest of diemensions
  const other = excludeSize.filter((dimension) => {
    if (!dimension.label.includes('Brand') && !dimension.label.includes('Color')
      && !dimension.label.includes('Finish') && !dimension.label.includes('Size')
      && (!dimension.label.includes('Review Rating')
        || (dimension.label.includes('Review Rating') && priority == null))) {
      return true;
    }
    return false;
  });
  other.sort(sortAscending);
  return [
    ...priorityDimension,
    ...brand,
    ...color,
    ...finish,
    ...size,
    ...other
  ];
};

export const getAppliedDimensions = (dimensionsList) => {
  if (Array.isArray(dimensionsList)) {
    return dimensionsList.map((dimension) => {
      return {
        label: dimension.label,
        refinements: dimension.refinements.slice()
      };
    });
  }
  return [];
};

const hasLessThan = (refinement) => {
  return ['Less', '<'].some((substring) => refinement.label.includes(substring));
};

const hasGreaterThan = (refinement) => {
  return ['Greater', '>'].some((substring) => refinement.label.includes(substring));
};

export const getRefinementRangeAndUrl = ({
  lowerBound,
  metadata = {},
  refinements = [],
  upperBound,
}) => {

  let refinementRange = [];
  let url = metadata?.canonicalUrl;

  refinements.forEach((refinement) => {
    // preemptively add delimiter to refinement key
    let delimitedRefinementKey = `Z${refinement.refinementKey}`;
    // clears any previously added refinements from the canonical url
    url = url.replace(delimitedRefinementKey, '');
    let refinementGreaterOrLessThan = '';

    if (hasLessThan(refinement)) {
      refinementGreaterOrLessThan = parseLabel(getRefinementLabel(refinement)) - 1;
    }

    if (hasGreaterThan(refinement)) {
      refinementGreaterOrLessThan = parseLabel(getRefinementLabel(refinement)) + 1;
    }
    // ensure refinement start range is being accounted for
    let refinementUnitMeasurement = refinementGreaterOrLessThan || parseLabel(getRefinementLabel(refinement)
      .split(' ')[0]);

    // round range bounds to the nearest multiple of 5
    const min = hasRange(refinement) ? Math.floor(lowerBound / 5) * 5 : lowerBound;
    // decrement measurement so it's nominally considered for range value
    const max = hasRange(refinement) ? (Math.ceil(upperBound / 5) * 5) - 1 : upperBound;

    if (refinementUnitMeasurement && (refinementUnitMeasurement <= max && refinementUnitMeasurement >= min)) {
      refinementRange.push(refinement);
    }
  }
  );

  return { refinements: refinementRange, url };
};

export const isRefinementActive = (refinementKey, currentSelectedRefinements) => {
  return Array.isArray(currentSelectedRefinements)
    && currentSelectedRefinements.some((refinement) => refinement.refinementKey === refinementKey);
};

export const findCustomPriceRange = (priceRefinements = []) => {
  let customPriceRange = priceRefinements
    .findLast((refinement) => (refinement.lowerbound && refinement.upperbound) || refinement.refinementKey === null);
  if (customPriceRange === undefined) {
    return [];
  }
  if (customPriceRange?.lowerbound || customPriceRange?.upperbound) {
    return [customPriceRange?.lowerbound, customPriceRange?.upperbound];
  }
  if (customPriceRange?.label.toLowerCase().includes('and up')) {
    let values = customPriceRange.label.split(' ');
    return [values[0].trim().replace('$', ''), ''];
  }
  let values = customPriceRange?.label.split('-');
  return [values[0].trim().replace('$', ''), values[1].trim().replace('$', '')];
};

// get dimensions from where refinements' labels are taken to be added to the URL
export const getDimensionsForUrl = (dimensions = []) => {
  if (!Array.isArray(dimensions)) return [];

  // Skip these dimension labels
  const excludedLabels = ['Price', 'Category', 'Saving by Percentage Off'];

  return dimensions.filter((dim, index) => {
    const isExcludedLabel = excludedLabels.includes(dim.label);
    // Special case for Review Rating
    const isValidRating = dim.label === 'Review Rating'
      && ['5', '4 & Up'].includes(dim?.refinements[0]?.label)
      && index === 0;

    return !isExcludedLabel || isValidRating;
  });
};

/**
 * get refinements' labels that will be added to the URL and
 * rerplaces spaces with '-'
 * */
export const getRefinemntsLabelsForUrl = (refinements = []) => {
  let skipOne = 1;
  if (refinements.length === 0) {
    return [];
  }
  let refinementLabels = refinements.map((refinement) => {
    let labelNoSpace = refinement.label.replace(/ /g, '-').replace(/#/g, '');
    if (labelNoSpace === '5') {
      skipOne = 0;
      return BEST_RATED;
    }
    if (labelNoSpace === '4-&-Up') {
      skipOne = 0;
      return HIGH_RATED;
    }
    return labelNoSpace;
  });

  return refinementLabels.slice(0, MAX_REFINEMENTS - skipOne);
};

/**
 * check if catStyle is in the currentUrl and pass it as a queryParam string for filters
 * @param queryParams
 */
export function filtersCatStyleCheck(queryParams) {
  let newQueryParams = '';
  // Check if 'catStyle' exists in current url
  if (window.location.href.includes('catStyle=')) {
    let queryAnd = '';
    if (queryParams.length > 0) {
      queryAnd = '&';
    }

    const currentUrlParams = new URLSearchParams(window.location.search);
    if (currentUrlParams.get('catStyle').toUpperCase() === 'SHOWPRODUCTS') {
      newQueryParams = queryParams + queryAnd + 'catStyle=' + currentUrlParams.get('catStyle');
    }
    return newQueryParams;
  }
  return queryParams;
}

export const onMultiStoreSelectionChange = ({ multiStoreIds }) => {
  const storeSelection = [...multiStoreIds].map((selectedStore) => selectedStore?.toString());
  window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('NEARBY_STORES.update', { nearByStores: storeSelection });
};

export const getSortNameValue = ({ key, order, keyword }) => {
  const sortTypes = keyword ? searchSortTypes : browseSortTypes;
  const sortByDropdown = key === 'price' ? sortTypes.find(
    (item) => item.sortby === key && item.sortorder === order)
    : sortTypes.find((item) => item.sortby === key) || {};

  const { name = 'Best Match', sortby, sortorder } = sortByDropdown;

  return {
    name,
    value: (sortby && sortorder) ? `${sortby}:${sortorder}` : ''
  };
};

export const sortOrder = ({ desktopSort, queryParams }) => {
  let updatedQueryParams = queryParams;
  if (desktopSort?.current?.length > 0) {
    const [sortedBy, orderedBy] = desktopSort?.current.split(':');
    updatedQueryParams = `${queryParams}&sortorder=${orderedBy}&sortby=${sortedBy}`;
  }
  return updatedQueryParams;
};

export const getMinValueFromLabel = (label) => {
  const labelTrimmed = label.trim();

  // Case: Over $X
  if (labelTrimmed.startsWith('Over')) {
    return labelTrimmed.replace('Over ', ''); // returns "$5000"
  }

  // Case: Under $X (no min)
  if (labelTrimmed.startsWith('Under')) {
    return '';
  }

  // Case: Extract the part before the hyphen
  if (labelTrimmed.includes('-')) {
    return labelTrimmed.substring(0, labelTrimmed.indexOf(' -'));
  }

  return null;
};

export const getMaxValueFromLabel = (label) => {
  const labelTrimmed = label.trim();

  // Case: Over $X (no max)
  if (labelTrimmed.startsWith('Over')) {
    return '';
  }

  // Case: Under $X
  if (labelTrimmed.startsWith('Under')) {
    return labelTrimmed.replace('Under ', '').trim();
  }

  // Case: Extract the part after the hyphen
  if (labelTrimmed.includes('-')) {
    return labelTrimmed.substring(labelTrimmed.indexOf('-') + 3);
  }

  return null;
};

// for appending currently selected selections in primary filter drawer
export const createDimensionWithRefinements = (label, dimensionsList) => {
  if (Array.isArray(dimensionsList)) {
    return [{
      label,
      refinements: dimensionsList
    }];
  }
  return [];
};

// for extracting rating from selected dimensions
export const extractRating = (text) => {
  if (!text) {
    return 0;
  }
  const match = text.match(/\d+/);
  return match ? parseInt(match[0], 10) : 0;
};

/**
 * Handles star rating refinement selections by maintaining only one rating filter at a time
 * Returns empty array when refinement is undefined/null/empty
 * Returns array with single refinement when valid rating provided
 *
 * @param {Object|undefined} refinement - Rating refinement object or undefined for clearing
 * @returns {Array} - [] for clearing rating, [refinement] for setting rating
 *
 * @example
 * // Clear rating selection
 * onRatingRefinements(undefined); // returns []
 * onRatingRefinements(null); // returns []
 *
 * // Set new rating
 * onRatingRefinements({
 *   label: '4 & Up',
 *   refinementKey: 'bwo5o'
 * }); // returns [{label: '4 & Up', refinementKey: 'bwo5o'}]
 */
export const onRatingRefinements = (refinement) => {
  return refinement
    ? [refinement]
    : [];
};

/**
 * Extracts the lower bound value from a range label
 * Takes a string label that contains a numeric value followed by other text
 * Returns the first part of the string before any spaces
 *
 * @param {string} label - Label containing numeric value (e.g. "10 inches" or "5 - 10 cm")
 * @returns {string} The first numeric value in the label
 *
 * @example
 * extractFirstValue("10 inches") // returns "10"
 * extractFirstValue("5 - 10 cm") // returns "5"
 */
export const extractFirstValue = (label) => {
  return label.split(' ')[0];
};

export const storeLifecycleEvent = (eventName, eventData) => {
  const sessionStorageKey = 'refinementsAnalyticsObj';
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }
  try {
    const dataToStore = { eventName, eventData };
    window.sessionStorage.setItem(sessionStorageKey, JSON.stringify(dataToStore));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Error storing event data for key ${sessionStorageKey}:`, error);
  }
};

export const getRatingValue = (refinements, isSecondaryDrawer) => {
  if (!refinements?.length) {
    return '';
  }

  if (isSecondaryDrawer) {
    // For secondary drawer, find Review Rating dimension and get its refinement
    const reviewRating = refinements.find((ref) => ref?.label === 'Review Rating');
    return reviewRating?.refinements?.[0]?.label || '';
  }

  // For primary drawer, directly get the last refinement's label
  return refinements[refinements.length - 1]?.label || '';
};

export const getIsMajorAppliance = (data) => {
  return data?.searchModel?.products?.some((product) => product?.identifiers?.productType === 'MAJOR_APPLIANCE');
};

export const manageDeliverySpeedSort = (sortTypes, shouldShow, isSortEnabled, deliverySpeedType) => {
  const existingIndex = sortTypes.findIndex(type => type.name === deliverySpeedType.name);
  const shouldAdd = shouldShow && isSortEnabled && existingIndex === -1;
  const shouldRemove = !shouldShow && existingIndex !== -1;

  if (shouldAdd) {
    sortTypes.splice(1, 0, deliverySpeedType);
  } else if (shouldRemove) {
    sortTypes.splice(existingIndex, 1);
  }

  return sortTypes;
};

/**
 * Adds a refinement to the dimension refinements array if it doesn't already exist
 */
export const addRefinementIfNotExists = (dimensionRefinements, newRefinement) => {
  const refinementKeysSet = new Set(dimensionRefinements.map((ref) => ref.refinementKey));
  if (!refinementKeysSet.has(newRefinement.refinementKey)) {
    dimensionRefinements.push(newRefinement);
  }
  return dimensionRefinements;
};

export const normalizePriceVal = (val) => {
  if (val == null || val === '') return null;
  return parseInt(String(val).replace(/\D/g, ''), 10); // remove non-digits
};

export const hasPriceRangesOverlap = (userLower, userUpper, refinementMin, refinementMax) => {
  // skip non-numeric ranges like 'Special Values'
  if (refinementMin == null && refinementMax == null) return false;

  const userMin = userLower ?? -Infinity;
  const userMax = userUpper ?? Infinity;
  const rangeMin = refinementMin ?? -Infinity;
  const rangeMax = refinementMax ?? Infinity;

  return userMin < rangeMax && rangeMin < userMax;
};

export const findMatchingPriceRange = (lowerBound, upperBound, refinements) => {
  let lower = normalizePriceVal(lowerBound);
  let upper = normalizePriceVal(upperBound);

  if (!refinements?.length) return [];

  if (lower != null && upper == null) {
    upper = Infinity; // FIXED
  }

  if (upper != null && lower == null) {
    lower = 0; // Or -Infinity, depending on your needs
  }

  if (lower == null || upper == null) return [];

  return refinements.filter((ref) => {
    const min = normalizePriceVal(getMinValueFromLabel(ref.label));
    const max = normalizePriceVal(getMaxValueFromLabel(ref.label));
    return hasPriceRangesOverlap(lower, upper, min, max);
  });
};

// Check if user-entered range exactly matches any existing refinement
export const hasExactPriceRangeMatch = (lowerBound, upperBound, refinements) => {
  const lower = normalizePriceVal(lowerBound);
  const upper = normalizePriceVal(upperBound);

  if (lower == null || upper == null || !refinements?.length) return false;

  return refinements.some((ref) => {
    const min = normalizePriceVal(getMinValueFromLabel(ref.label));
    const max = normalizePriceVal(getMaxValueFromLabel(ref.label));
    return min === lower && max === upper;
  });
};

const isDeliveryLabel = (label) => label === FULFILLMENT_LABELS.DELIVERY || label === FULFILLMENT_LABELS.FREE_DELIVERY;

export const transformFulfillmentDimension = (dim) => {
  const refinementLabel = dim.refinements[0]?.label;
  const dimensionObj = {
    ...dim,
    label: GET_IT_FAST_DIMENSION_LABEL
  };

  if (isDeliveryLabel(dim.label)) {
    dimensionObj.analytics = {
      freeDelivery: dim.label === FULFILLMENT_LABELS.FREE_DELIVERY ? 'y' : 'n',
      speed: refinementLabel,
      value: FULFILLMENT_LABELS.DELIVERY
    };
  }

  if (refinementLabel === REFINEMENTS.PICKUP_TODAY || refinementLabel === FULFILLMENT_LABELS.IN_STORE) {
    dimensionObj.analytics = {
      nearbyStore: 'y',
      value: FULFILLMENT_LABELS.IN_STORE
    };
  }

  return dimensionObj;
};

export const isPlpFulfillmentLabel = (label) => {
  const plpFulfillmentLabels = [
    FULFILLMENT_LABELS.FREE_DELIVERY,
    FULFILLMENT_LABELS.DELIVERY,
    IN_STORE_CHIP_DIMENSION_LABEL,
    PLP_SPEED_DIMENSION_LABEL,
    FULFILLMENT_LABELS.BROWSE_ALL,
    GET_IT_FAST_DIMENSION_LABEL
  ];

  return plpFulfillmentLabels.includes(label);
};
